<template>
  <div class="simulation-history-page">
    <div class="page-header">
      <h1>{{ t('simulation.history.title') }}</h1>
      <router-link to="/simulation/create" class="btn btn-primary create-simulation-btn">
        {{ t('simulation.history.actions.create') }}
      </router-link>
    </div>

    <div class="filters-section">
      <div class="filter-group">
        <label for="filter-created-after">{{ t('simulation.history.filters.createdAfter') }}</label>
        <input type="date" id="filter-created-after" v-model="filters.createdAfter" :lang="dateInputLang">
      </div>
      <div class="filter-group">
        <label for="filter-created-before">{{ t('simulation.history.filters.createdBefore') }}</label>
        <input type="date" id="filter-created-before" v-model="filters.createdBefore" :lang="dateInputLang">
      </div>
      <div class="filter-group">
        <label for="filter-strategy">{{ t('simulation.history.filters.strategy') }}</label>
        <select id="filter-strategy" v-model="filters.strategyName">
          <option value="">{{ t('simulation.history.filters.strategyAll') }}</option>
          <option v-for="name in uniqueStrategyNames" :key="name" :value="name">{{ name }}</option>
        </select>
      </div>
      <div class="filter-group">
        <label for="filter-status">{{ t('simulation.history.filters.status.label') }}</label>
        <select id="filter-status" v-model="filters.status">
          <option v-for="option in statusOptions" :key="option.value || 'all'" :value="option.value">
            {{ option.label }}
          </option>
        </select>
      </div>
      <div class="filter-group">
        <label for="filter-sim-name">{{ t('simulation.history.filters.name') }}</label>
        <input
          type="text"
          id="filter-sim-name"
          v-model.lazy="filters.simulationName"
          :placeholder="t('simulation.history.filters.namePlaceholder')">
      </div>
      <div class="filter-actions">
        <button @click="clearFiltersAndSort" class="btn btn-secondary">
          {{ t('simulation.history.filters.clear') }}
        </button>
      </div>
    </div>

    <div v-if="isLoading && !simulations.some(s => s.isCancelling || s.isRunningDraft)" class="loading-state">
      <p>{{ t('simulation.history.loading') }}</p>
      <div class="spinner"></div>
    </div>

    <div v-else-if="error" class="error-state">
      <p class="error-message">{{ t('simulation.history.error', { message: error }) }}</p>
      <button @click="fetchSimulations" class="btn btn-secondary">{{ t('common.retry') }}</button>
    </div>

    <div v-else-if="simulations.length === 0 && !Object.values(filters).some(f => f !== '')" class="empty-state">
      <p>{{ t('simulation.history.empty.noRecords') }}</p>
      <p>{{ t('simulation.history.empty.hint') }}</p>
    </div>
    <div v-else-if="paginatedSimulations.length === 0" class="empty-state">
       <p>{{ t('simulation.history.empty.noResults') }}</p>
    </div>

    <!-- Wrapper for when simulations are present and should be displayed -->
    <div v-else>
      <div class="compare-action-bar" v-if="simulations.length > 0 && paginatedSimulations.length > 0">
        <button
          @click="navigateToComparePage"
          :disabled="compareButtonDisabled"
          class="btn btn-primary compare-button">
          {{ t('simulation.history.compare.button', { count: selectedSimulationsForCompare.length }) }}
        </button>
        <span v-if="compareButtonDisabled && selectedSimulationsForCompare.length > 0 && (selectedSimulationsForCompare.length < 2 || selectedSimulationsForCompare.length > 5)" class="compare-hint">
          {{ t('simulation.history.compare.hint') }}
        </span>
      </div>

      <div class="history-table-container">
        <table>
          <thead>
            <tr>
            <th class="checkbox-column-header">{{ t('simulation.history.table.select') }}</th>
            <th @click="setSort('name')" class="sortable-header">
              {{ t('simulation.history.table.name') }}
              <span v-if="sortKey === 'name'" class="sort-indicator">{{ sortOrder === 'asc' ? '▲' : '▼' }}</span>
            </th>
            <th @click="setSort('created_at')" class="sortable-header">
              {{ t('simulation.history.table.createdAt') }}
              <span v-if="sortKey === 'created_at'" class="sort-indicator">{{ sortOrder === 'asc' ? '▲' : '▼' }}</span>
            </th>
            <th>{{ t('simulation.history.table.period') }}</th>
            <th @click="setSort('asset_count')" class="sortable-header">
              {{ t('simulation.history.table.assetCount') }}
               <span v-if="sortKey === 'asset_count'" class="sort-indicator">{{ sortOrder === 'asc' ? '▲' : '▼' }}</span>
            </th>
            <th @click="setSort('strategy_name')" class="sortable-header">
              {{ t('simulation.history.table.strategy') }}
              <span v-if="sortKey === 'strategy_name'" class="sort-indicator">{{ sortOrder === 'asc' ? '▲' : '▼' }}</span>
            </th>
            <th @click="setSort('status')" class="sortable-header">
              {{ t('simulation.history.table.status') }}
              <span v-if="sortKey === 'status'" class="sort-indicator">{{ sortOrder === 'asc' ? '▲' : '▼' }}</span>
            </th>

            <th>{{ t('simulation.history.table.actions') }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="sim in paginatedSimulations" :key="sim.id">
            <td class="checkbox-cell">
              <input
                type="checkbox"
                v-if="sim.status === 'Completed'"
                :value="sim.id"
                v-model="selectedSimulationsForCompare">
                <!-- Removed @change="handleCompareSelectionChange(sim.id)" as v-model is sufficient for now -->
            </td>
            <td>{{ sim.name }}</td>
            <td>{{ displayableDateTime(sim.created_at) }}</td>
            <td>{{ t('simulation.history.table.periodRange', { start: displayableDate(sim.parameters.start_date), end: displayableDate(sim.parameters.end_date) }) }}</td>
            <td>{{ sim.asset_count ?? t('common.notAvailable') }}</td>
            <td>{{ sim.strategy_name ?? t('common.notAvailable') }}</td>
            <td>
              <span :class="statusClass(sim.status)" :title="sim.status === 'Failed' && sim.errorReason ? t('simulation.history.tooltips.failedReason', { reason: sim.errorReason }) : translateStatus(sim.status)">
                {{ translateStatus(sim.status) }}
              </span>
              <div v-if="sim.status === 'Failed' && sim.errorReason"
                   class="status-details error-reason-display">
                ({{ truncateText(sim.errorReason, 30) }})
              </div>
            </td>

            <td class="actions-cell">
               <button
                v-if="sim.status === 'Queued' || sim.status === 'Pending'"
                @click="handleCancelSimulation(sim.id, sim.name)"
                class="btn btn-sm btn-outline-warning cancel-button"
                :disabled="sim.isCancelling || sim.isRunningDraft">
                {{ sim.isCancelling ? t('simulation.history.actions.cancelProcessing') : t('simulation.history.actions.cancel') }}
              </button>
               <button
                v-if="sim.status === 'Draft'"
                @click="handleRunDraft(sim)"
                class="btn btn-sm btn-success run-draft-button"
                :disabled="sim.isRunningDraft || sim.isCancelling">
                {{ sim.isRunningDraft ? t('simulation.history.actions.runDraftProcessing') : t('simulation.history.actions.runDraft') }}
              </button>
              <router-link
                :to="`/simulation/results/${sim.id}`"
                class="btn btn-sm btn-info view-results-link"
                :class="{ 'disabled-link': sim.status !== 'Completed' }"
                :event="sim.status !== 'Completed' ? '' : 'click'"
                :title="t('simulation.history.actions.viewResults')"
              >
                {{ t('simulation.history.actions.viewResults') }}
              </router-link>
              <button @click="handleCopySettings(sim)" class="btn btn-sm btn-secondary" :title="t('simulation.history.actions.copySettings')">
                {{ t('simulation.history.actions.copySettings') }}
              </button>
              <button @click="handleRerun(sim)" class="btn btn-sm btn-warning" :title="t('simulation.history.actions.rerun')">
                {{ t('simulation.history.actions.rerun') }}
              </button>
              <button @click="confirmDeleteSimulation(sim.id, sim.name)" class="btn btn-sm btn-danger" :title="t('simulation.history.actions.delete')">
                {{ t('simulation.history.actions.delete') }}
              </button>
            </td>
          </tr>
          </tbody>
        </table>
        <div class="pagination-controls" v-if="totalPages > 1">
          <button @click="prevPage" :disabled="currentPage === 1" class="btn btn-outline-secondary">
            &laquo; {{ t('simulation.history.pagination.previous') }}
          </button>
          <span>
            {{ t('simulation.history.pagination.summary', { current: currentPage, total: totalPages }) }}
          </span>
          <button @click="nextPage" :disabled="currentPage === totalPages" class="btn btn-outline-secondary">
            {{ t('simulation.history.pagination.next') }} &raquo;
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
/**
 * @file SimulationHistoryPage.vue
 * @description Page for displaying, filtering, sorting, and paginating simulation history records.
 * Allows users to view past simulations, manage them (delete, copy settings, re-run, cancel, run draft),
 * and navigate to detailed results. Failure reasons for 'Failed' simulations are also displayed.
 */
import { ref, onMounted, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useDateInputLang } from '@/composables/useDateInputLang.js';
import { useRouter } from 'vue-router';
import { formatDate, formatDateTime } from '@/utils/date.js';
import {
  getSimulationHistory as apiGetSimulationHistory,
  deleteSimulation as apiDeleteSimulation,
  cancelSimulation as apiCancelSimulation,
  runDraftSimulation as apiRunDraftSimulation
} from '@/services/api.js';

/**
 * Stores the full list of simulation records fetched from the API.
 * Each simulation object can have temporary UI state flags like `isCancelling` or `isRunningDraft`.
 * @type {import('vue').Ref<Array<object>>}
 */
const simulations = ref([]);

/**
 * Tracks the loading state during API calls (fetch, delete, cancel, run draft).
 * @type {import('vue').Ref<boolean>}
 */
const isLoading = ref(false);

/**
 * Stores any error message encountered during data fetching.
 * @type {import('vue').Ref<string | null>}
 */
const error = ref(null);
const router = useRouter();

/**
 * @typedef {object} Filters
 * @property {string} createdAfter - YYYY-MM-DD format. Filters simulations created on or after this date.
 * @property {string} createdBefore - YYYY-MM-DD format. Filters simulations created on or before this date.
 * @property {string} strategyName - Filters by exact strategy name. Empty for all.
 * @property {string} status - Filters by exact status. Empty for all.
 * @property {string} simulationName - Filters by case-insensitive partial match on simulation name.
 */
const filters = ref({
  createdAfter: '',
  createdBefore: '',
  strategyName: '',
  status: '',
  simulationName: ''
});

/**
 * Stores the IDs of simulations selected for comparison.
 * @type {import('vue').Ref<string[]>}
 */
const selectedSimulationsForCompare = ref([]);

/**
 * Reactive array storing unique strategy names extracted from the simulations list,
 * used to populate the strategy filter dropdown.
 * @type {import('vue').Ref<string[]>}
 */
const uniqueStrategyNames = ref([]);

/**
 * Reactive string indicating the current key used for sorting the simulations list.
 * @type {import('vue').Ref<string>}
 */
const sortKey = ref('created_at');

/**
 * Reactive string indicating the current sort order ('asc' for ascending, 'desc' for descending).
 * @type {import('vue').Ref<'asc' | 'desc'>}
 */
const sortOrder = ref('desc');

/**
 * Reactive number representing the current page in pagination.
 * @type {import('vue').Ref<number>}
 */
const currentPage = ref(1);

/**
 * Reactive number defining how many items are displayed per page.
 * @type {import('vue').Ref<number>}
 */
const itemsPerPage = ref(10);

const { t } = useI18n();
const { dateInputLang } = useDateInputLang();

const statusOptions = computed(() => [
  { value: '', label: t('simulation.history.filters.status.all') },
  { value: 'Draft', label: translateStatus('Draft') },
  { value: 'Queued', label: translateStatus('Queued') },
  { value: 'Pending', label: translateStatus('Pending') },
  { value: 'Completed', label: translateStatus('Completed') },
  { value: 'Failed', label: translateStatus('Failed') },
  { value: 'Cancelled', label: translateStatus('Cancelled') }
]);

function translateStatus(status) {
  if (!status) {
    return '';
  }
  const key = status.toLowerCase();
  const translationKey = `simulation.history.status.${key}`;
  const translated = t(translationKey, { status });
  return translated === translationKey ? status : translated;
}

/**
 * Formats a date string into 'YYYY-MM-DD' format for display.
 * Returns 'N/A' if the dateString is invalid or formatting fails.
 * @param {string | undefined} dateString - The date string to format.
 * @returns {string} The formatted date or 'N/A'.
 */
const displayableDate = (dateString) => {
  if (!dateString) return t('common.notAvailable');
  try {
    return formatDate(new Date(dateString));
  } catch (e) {
    console.error("Error formatting date:", dateString, e);
    return dateString ?? t('common.notAvailable');
  }
};

/**
 * Formats a date string into detailed datetime format for display.
 * Returns 'N/A' if the dateString is invalid or formatting fails.
 * @param {string | undefined} dateString - The date string to format.
 * @returns {string} The formatted datetime or 'N/A'.
 */
const displayableDateTime = (dateString) => {
  if (!dateString) return t('common.notAvailable');
  try {
    return formatDateTime(new Date(dateString));
  } catch (e) {
    console.error("Error formatting datetime:", dateString, e);
    return dateString ?? t('common.notAvailable');
  }
};

/**
 * Fetches simulation history from the API.
 * Initializes temporary UI state flags (`isCancelling`, `isRunningDraft`) for each simulation.
 * The `isCancelling` flag is set to true when cancellation for a simulation is initiated,
 * and reset to false when the API call completes or fails. It's used to disable buttons
 * and show a "Cancelling..." state.
 * The `isRunningDraft` flag is set to true when running a draft is initiated,
 * and reset to false when the API call completes or fails. It's used to disable buttons
 * and show an "Executing..." state.
 */
const fetchSimulations = async () => {
  isLoading.value = true;
  error.value = null;
  try {
    const response = await apiGetSimulationHistory();
    const rawSimulations = Array.isArray(response) ? response : [];
    simulations.value = rawSimulations.map(sim => ({
      ...sim,
      isCancelling: false,
      isRunningDraft: false
    }));
    const strategyNames = new Set(rawSimulations.map(s => s.strategy_name).filter(Boolean));
    uniqueStrategyNames.value = Array.from(strategyNames);
  } catch (err) {
    console.error('Error fetching simulation history:', err);
    error.value = err.message || t('simulation.history.messages.unexpectedError');
    simulations.value = [];
    uniqueStrategyNames.value = [];
  } finally {
    isLoading.value = false;
  }
};

/**
 * Confirms and handles the deletion of a simulation.
 * @param {string} simulationId - The ID of the simulation to delete.
 * @param {string} simulationName - The name for confirmation.
 */
const confirmDeleteSimulation = async (simulationId, simulationName) => {
  if (!window.confirm(t('simulation.history.messages.confirmDelete', { name: simulationName }))) {
    return;
  }

  isLoading.value = true;
  try {
    const response = await apiDeleteSimulation(simulationId);
    if (response.success) {
      simulations.value = simulations.value.filter(s => s.id !== simulationId);
      window.alert(t('simulation.history.messages.deleteSuccess', { name: simulationName }));
    } else {
      throw new Error(response.message || t('simulation.history.messages.deleteFailureFallback'));
    }
  } catch (err) {
    console.error(`Error deleting simulation ${simulationId}:`, err);
    const reason = err?.message || t('common.unknownError');
    window.alert(t('simulation.history.messages.deleteFailure', { reason }));
  } finally {
    isLoading.value = false;
  }
};

/**
 * Handles copying simulation settings to the create page.
 * @param {object} simulation - The simulation object.
 */
const handleCopySettings = (simulation) => {
  router.push({ name: 'CreateSimulation', query: { fromHistoryId: simulation.id } });
};

/**
 * Placeholder for re-running a simulation.
 * @param {object} simulation - The simulation object.
 */
const handleRerun = (simulation) => {
  console.log('Re-run simulation:', simulation);
  window.alert(t('simulation.history.messages.rerunNotImplemented'));
};

onMounted(() => {
  fetchSimulations();
});

/**
 * Processes simulations by filtering and then sorting them.
 * @returns {Array<object>} Filtered and sorted simulations.
 */
const processedSimulations = computed(() => {
  let items = [...simulations.value];
  // Apply filters (same logic as before)
  if (filters.value.createdAfter) { const ad = new Date(filters.value.createdAfter); ad.setHours(0,0,0,0); items = items.filter(sim => new Date(sim.created_at) >= ad); }
  if (filters.value.createdBefore) { const bd = new Date(filters.value.createdBefore); bd.setHours(23,59,59,999); items = items.filter(sim => new Date(sim.created_at) <= bd); }
  if (filters.value.strategyName) { items = items.filter(sim => sim.strategy_name === filters.value.strategyName); }
  if (filters.value.status) { items = items.filter(sim => sim.status === filters.value.status); }
  if (filters.value.simulationName) { const st = filters.value.simulationName.toLowerCase(); items = items.filter(sim => sim.name.toLowerCase().includes(st)); }

  // Apply sorting (same logic as before)
  const getNestedValue = (obj, path) => { if (!obj || !path) return undefined; const parts = path.split('.'); let current = obj; for (const part of parts) { if (current && typeof current === 'object' && current !== null && part in current) { current = current[part]; } else { return undefined; } } return current; };
  items.sort((a, b) => { let valA = getNestedValue(a, sortKey.value); let valB = getNestedValue(b, sortKey.value); if (sortKey.value === 'created_at') { valA = valA ? new Date(valA).getTime() : 0; valB = valB ? new Date(valB).getTime() : 0; } else if (['asset_count'].includes(sortKey.value)) { valA = (valA === undefined || valA === null) ? -Infinity : Number(valA); valB = (valB === undefined || valB === null) ? -Infinity : Number(valB); } else if (typeof valA === 'string' && typeof valB === 'string') { valA = valA.toLowerCase(); valB = valB.toLowerCase(); } else { valA = (valA === undefined || valA === null) ? '' : String(valA).toLowerCase(); valB = (valB === undefined || valB === null) ? '' : String(valB).toLowerCase(); } if (valA === valB) return 0; let comparison = valA > valB ? 1 : -1; return sortOrder.value === 'asc' ? comparison : -comparison; });
  return items;
});

/**
 * Sets the sort key and order.
 * @param {string} key - The key to sort by.
 */
const setSort = (key) => {
  if (sortKey.value === key) {
    sortOrder.value = sortOrder.value === 'asc' ? 'desc' : 'asc';
  } else {
    sortKey.value = key;
    switch (key) {
      case 'created_at': case 'asset_count':
        sortOrder.value = 'desc'; break;
      default: sortOrder.value = 'asc'; break;
    }
  }
};

/**
 * Clears filters, resets sorting, and goes to the first page.
 * Also clears any selected simulations for comparison.
 */
const clearFiltersAndSort = () => {
  filters.value = { createdAfter: '', createdBefore: '', strategyName: '', status: '', simulationName: '' };
  sortKey.value = 'created_at';
  sortOrder.value = 'desc';
  currentPage.value = 1;
  selectedSimulationsForCompare.value = []; // Clear selections
};

/**
 * Computed property to determine if the "Compare Selected Simulations" button should be disabled.
 * Disabled if fewer than 2 or more than 5 simulations are selected.
 * @type {import('vue').ComputedRef<boolean>}
 */
const compareButtonDisabled = computed(() => {
  const count = selectedSimulationsForCompare.value.length;
  return count < 2 || count > 5;
});

/**
 * Placeholder method for when the "Compare Selected Simulations" button is clicked.
 * Logs selected IDs to the console and shows an alert.
 * Actual navigation and comparison logic will be implemented in a future step.
 */
const navigateToComparePage = () => {
  if (compareButtonDisabled.value) {
    window.alert(t('simulation.history.messages.compareSelection'));
    return;
  }
  if (selectedSimulationsForCompare.value.length > 0) {
    const ids = selectedSimulationsForCompare.value.join(',');
    router.push({ path: '/simulation/compare', query: { ids: ids } });
  } else {
    // This case should ideally be prevented by the button being disabled if length is 0,
    // but as a fallback:
    window.alert(t('simulation.history.messages.compareSelectFirst'));
  }
};

/**
 * Total pages for pagination.
 * @returns {number}
 */
const totalPages = computed(() => {
  if (!processedSimulations.value.length) return 1;
  return Math.ceil(processedSimulations.value.length / itemsPerPage.value);
});

/**
 * Simulations for the current page.
 * @returns {Array<object>}
 */
const paginatedSimulations = computed(() => {
  const startIndex = (currentPage.value - 1) * itemsPerPage.value;
  const endIndex = startIndex + itemsPerPage.value;
  return processedSimulations.value.slice(startIndex, endIndex);
});

/** Navigates to the next page. */
const nextPage = () => { if (currentPage.value < totalPages.value) currentPage.value++; };
/** Navigates to the previous page. */
const prevPage = () => { if (currentPage.value > 1) currentPage.value--; };

/** Resets to page 1 when filters or sort order change. */
watch([filters, sortKey, sortOrder], () => {
  currentPage.value = 1;
  selectedSimulationsForCompare.value = []; // Clear selections when filters/sort change
}, { deep: true });

/**
 * Truncates text to a maximum length, adding '...' if truncated.
 * Utility function for displaying potentially long strings in a confined space.
 * @param {string | null | undefined} text - The text to truncate.
 * @param {number} maxLength - The maximum length before truncation.
 * @returns {string} The truncated text, or the original text if shorter than maxLength, or an empty string if input is null/undefined.
 */
const truncateText = (text, maxLength) => {
  if (!text) return '';
  return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
};

/**
 * Handles cancelling a simulation.
 * Prompts the user for confirmation before proceeding.
 * Sets the `isCancelling` flag on the specific simulation object to true during the API call
 * and resets it in a finally block. Updates the simulation list with the new status on success.
 * @async
 * @param {string} simulationId - The ID of the simulation to cancel.
 * @param {string} simulationName - The name of the simulation, used in the confirmation dialog.
 */
const handleCancelSimulation = async (simulationId, simulationName) => {
  if (!window.confirm(t('simulation.history.messages.confirmCancel', { name: simulationName }))) {
    return;
  }

  const sim = simulations.value.find(s => s.id === simulationId);
  if (sim) sim.isCancelling = true;

  try {
    const response = await apiCancelSimulation(simulationId);
    if (response.success && response.updatedSimulation) {
      const idx = simulations.value.findIndex(s => s.id === simulationId);
      // Update the simulation in the list with the new data from API, reset flags
      if (idx !== -1) simulations.value[idx] = {
        ...simulations.value[idx],
        ...response.updatedSimulation,
        isCancelling: false,
        isRunningDraft: false
      };
      window.alert(t('simulation.history.messages.cancelSuccess', { name: simulationName }));
    } else {
      throw new Error(response.message || t('simulation.history.messages.cancelFailureFallback'));
    }
  } catch (err) {
    console.error(`Error cancelling simulation ${simulationId}:`, err);
    const reason = err?.message || t('common.unknownError');
    window.alert(t('simulation.history.messages.cancelFailure', { name: simulationName, reason }));
    if (sim) sim.isCancelling = false; // Reset flag on error
  }
};

/**
 * Returns a dynamic CSS class string based on the simulation status.
 * Used to color-code the status display. Covers 'Draft', 'Queued', 'Pending',
 * 'Completed', 'Failed', and 'Cancelled' statuses.
 * @param {string | undefined} status - The status string of the simulation.
 * @returns {string} A CSS class name like 'status-completed', 'status-draft', etc. Returns empty string if status is undefined.
 */
const statusClass = (status) => {
  if (!status) return '';
  return `status-${status.toLowerCase()}`;
};

/**
 * Handles running a draft simulation.
 * Prompts the user for a name for the new simulation run.
 * Sets the `isRunningDraft` flag on the specific draft simulation object to true during the API call
 * and resets it in a finally block. Refreshes the simulation list on success to show the new run.
 * @async
 * @param {object} draftSimulation - The draft simulation object from which to create a new run.
 *                                   Expected to have at least `id` and `name` properties.
 */
const handleRunDraft = async (draftSimulation) => {
  const defaultRunName = t('simulation.history.messages.runDraftDefaultName', {
    name: draftSimulation.name,
    time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
  });
  const runName = window.prompt(
    t('simulation.history.messages.runDraftPrompt', { name: draftSimulation.name }),
    defaultRunName
  );

  if (runName === null) return; // User cancelled the prompt
  if (runName.trim() === '') {
    window.alert(t('simulation.history.messages.runDraftEmpty'));
    return;
  }

  const simInList = simulations.value.find(s => s.id === draftSimulation.id);
  if (simInList) simInList.isRunningDraft = true;

  try {
    // Pass the entire draftSimulation object instead of just its ID
    const response = await apiRunDraftSimulation(draftSimulation, runName.trim());
    if (response.success && response.newSimulation) {
      window.alert(t('simulation.history.messages.runDraftSuccess', { name: response.newSimulation.name }));
      fetchSimulations(); // Refresh the list to show the new simulation and reset states
    } else {
      throw new Error(response.message || t('simulation.history.messages.runDraftFailureFallback'));
    }
  } catch (err) {
    console.error(`Error running draft simulation ${draftSimulation.id}:`, err);
    const reason = err?.message || t('common.unknownError');
    window.alert(t('simulation.history.messages.runDraftFailure', { name: draftSimulation.name, reason }));
    if (simInList) simInList.isRunningDraft = false; // Reset flag on error if not refreshing list
  }
  // If fetchSimulations() is called, it will reset isRunningDraft for all items,
  // so the finally block might be redundant if success path always calls fetchSimulations.
  // However, keeping it for robustness in case fetchSimulations is removed or conditional.
  finally {
    if (simInList && simInList.isRunningDraft) { // Check if still true, as fetchSimulations might have already reset it
       simInList.isRunningDraft = false;
    }
  }
};
</script>

<style scoped>
.simulation-history-page {
  padding: 20px;
  max-width: 1200px;
  margin: 0 auto;
  font-family: Arial, sans-serif;
}

.compare-action-bar {
  display: flex;
  align-items: center;
  gap: 10px; /* Space between button and hint */
  margin-bottom: 15px; /* Space above the table or other content */
  padding: 10px;
  background-color: #f8f9fa; /* Light background for the bar */
  border-radius: 6px;
  border: 1px solid #e9ecef; /* Subtle border */
}
.compare-button {
  /* General .btn .btn-primary styles will apply. Add specific overrides if needed. */
  /* Example: font-size: 0.95em; */
}
.compare-button:disabled {
  /* General .btn:disabled styles will apply. */
  /* opacity: 0.65; */ /* Already in .btn:disabled typically */
  /* cursor: not-allowed; */ /* Already in .btn:disabled typically */
}
.compare-hint {
  font-size: 0.85em;
  color: #6c757d; /* Bootstrap's secondary text color */
  margin-left: 5px; /* Slight space from the count in the button */
}


.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 25px;
  padding-bottom: 15px;
  border-bottom: 2px solid #e9ecef;
}

.page-header h1 {
  text-align: left;
  margin-bottom: 0;
}

.create-simulation-btn {
  padding: 10px 20px;
  font-size: 1em;
  font-weight: 500;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.create-simulation-btn:hover {
  text-decoration: none;
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(0, 123, 255, 0.3);
}

h1 {
  text-align: center;
  margin-bottom: 25px;
}

.filters-section {
  display: flex;
  flex-wrap: wrap;
  gap: 15px;
  padding: 15px;
  background-color: #f9f9f9;
  border-radius: 8px;
  margin-bottom: 20px;
  border: 1px solid #eee;
}

.filter-group {
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.filter-group label {
  font-size: 0.9em;
  color: #555;
  margin-bottom: 2px;
}

.filter-group input[type="date"],
.filter-group input[type="text"],
.filter-group select {
  padding: 8px 10px;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-size: 0.95em;
}
.filter-group input[type="text"] {
    min-width: 200px;
}

.filter-actions {
  display: flex;
  align-items: flex-end;
  margin-left: auto;
}
.filter-actions .btn {
    height: 38px;
}

.loading-state, .error-state, .empty-state {
  text-align: center;
  padding: 40px;
  color: #555;
}

.error-message {
  color: #dc3545;
  margin-bottom: 10px;
}

.spinner {
  border: 4px solid rgba(0, 0, 0, 0.1);
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border-left-color: #007bff;
  animation: spin 1s ease infinite;
  margin: 20px auto;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.history-table-container {
  overflow-x: auto;
}

table {
  width: 100%;
  border-collapse: collapse;
  margin-top: 20px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
}

th, td {
  border: 1px solid #ddd;
  padding: 10px 12px;
  text-align: left;
  vertical-align: middle;
}
.checkbox-column-header {
  width: 60px; /* Adjust as needed for the checkbox */
  text-align: center;
  font-weight: normal; /* Optional: make 'Select' less prominent than sortable headers */
  color: #555;
}
.checkbox-cell {
  text-align: center;
  vertical-align: middle;
}
.checkbox-cell input[type="checkbox"] {
  cursor: pointer;
  width: 16px; /* Custom size for checkbox */
  height: 16px;
  accent-color: #007bff; /* Optional: theme color for the checkbox */
}

/* Ensure created time column has enough width for datetime display */
th:nth-child(3), td:nth-child(3) {
  min-width: 160px;
  white-space: nowrap;
}


th {
  background-color: #f8f9fa;
  font-weight: bold;
  white-space: nowrap;
}
.sortable-header {
  cursor: pointer;
  user-select: none;
}
.sortable-header:hover {
  background-color: #e9ecef;
}
.sortable-header .sort-indicator {
  margin-left: 5px;
  font-size: 0.9em;
  color: #007bff;
}

tr:nth-child(even) {
  background-color: #f9f9f9;
}

tr:hover {
  background-color: #f1f1f1;
}

.actions-cell {
  white-space: nowrap;
}

.actions-cell .btn, .actions-cell .view-results-link, .cancel-button, .run-draft-button { /* Added .run-draft-button */
  margin-right: 5px;
  margin-bottom: 5px;
}
.actions-cell .btn:last-child, .actions-cell .view-results-link:last-child, .cancel-button:last-child, .run-draft-button:last-child {
  margin-right: 0;
}

.btn {
  padding: 6px 12px;
  border-radius: 4px;
  text-decoration: none;
  cursor: pointer;
  font-size: 0.9em;
  border: 1px solid transparent;
}
.btn-primary { background-color: #007bff; color: white; }
.btn-secondary { background-color: #6c757d; color: white; }
.btn-info { background-color: #17a2b8; color: white; }
.btn-warning { background-color: #ffc107; color: #212529; }
.btn-danger { background-color: #dc3545; color: white; }
.btn-success { background-color: #28a745; color: white; } /* Added for Run Draft */
.btn-sm { padding: 5px 10px; font-size: 0.8em; }

.btn-outline-danger {
    color: #dc3545;
    border-color: #dc3545;
    background-color: transparent;
}
.btn-outline-danger:hover {
    color: #fff;
    background-color: #dc3545;
    border-color: #dc3545;
}
.btn-outline-warning {
    color: #ffc107;
    border-color: #ffc107;
    background-color: transparent;
}
.btn-outline-warning:hover {
    color: #212529;
    background-color: #ffc107;
    border-color: #ffc107;
}
.btn-outline-secondary {
    color: #6c757d;
    border-color: #6c757d;
    background-color: transparent;
}
.btn-outline-secondary:hover {
    color: #fff;
    background-color: #6c757d;
    border-color: #6c757d;
}


.disabled-link {
  pointer-events: none;
  opacity: 0.65;
  background-color: #6c757d;
  border-color: #6c757d;
  color: white;
}
.disabled-link:hover {
    background-color: #6c757d;
    color: white;
}

.status {
  padding: 3px 8px;
  border-radius: 4px;
  font-size: 0.85em;
  font-weight: bold;
  text-transform: capitalize;
  display: inline-block;
}
.status-queued { background-color: #ffc107; color: #333; }
.status-pending { background-color: #17a2b8; color: white; }
.status-completed { background-color: #28a745; color: white; }
.status-failed { background-color: #dc3545; color: white; }
.status-cancelled { background-color: #adb5bd; color: #343a40; }
.status-draft { background-color: #6f42c1; color: white; } /* Purple for Draft */


.status-details {
  font-size: 0.8em;
  color: #666;
  margin-top: 2px;
  display: block;
}
.error-reason-display {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 150px;
  display: inline-block;
  vertical-align: bottom;
  font-style: italic;
  font-size: 0.9em;
}


.cancel-button, .run-draft-button {
  /* Specific styles if needed, otherwise defaults to btn-sm and btn-outline-* or btn-* */
}
.cancel-button:disabled, .run-draft-button:disabled {
    opacity: 0.65;
    cursor: not-allowed;
}


.pagination-controls {
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: 20px;
  padding: 10px 0;
  gap: 10px;
}

.pagination-controls button {
  padding: 8px 12px;
}
.pagination-controls button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
.pagination-controls span {
  margin: 0 10px;
  font-size: 0.9em;
  color: #555;
}

</style>
